import React, { useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Autocomplete,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../../firebaseConfig";
import { toast } from "react-toastify";
import close from "../../../assets/svg/drawer_close.svg";
import Countries from "../../../Contries.json";

const validationSchema = Yup.object({
  country_list: Yup.array().when("apply_to_all_countries", {
    is: false,
    then: () =>
      Yup.array().min(
        1,
        "Select at least one country or enable 'Apply to all countries.'",
      ),
    otherwise: () => Yup.array(),
  }),
  country_targeting_mode: Yup.string().when("apply_to_all_countries", {
    is: false,
    then: () =>
      Yup.string().required("Select Include or Exclude for country targeting."),
    otherwise: () => Yup.string(),
  }),
});

export default function EditServiceModal({ open, onClose, serviceData }) {
  const formik = useFormik({
    initialValues: {
      apply_to_all_countries: false,
      country_list: [],
      country_targeting_mode: "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (!serviceData?.id) {
        toast.error("Service ID is missing.");
        return;
      }
      try {
        const serviceDocRef = doc(db, "couriers", serviceData.id);
        await updateDoc(serviceDocRef, {
          apply_to_all_countries: values.apply_to_all_countries,
          country_list: values.country_list,
          country_targeting_mode: values.country_targeting_mode,
        });

        toast.success("Service features updated successfully.");
        onClose();
      } catch (error) {
        console.error("Error updating document:", error);
        toast.error("Failed to update service features.");
      }
    },
  });

  useEffect(() => {
    if (serviceData && open) {
      formik.setValues({
        apply_to_all_countries: serviceData.apply_to_all_countries || false,
        country_list: serviceData.country_list || [],
        country_targeting_mode: serviceData.country_targeting_mode || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceData, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box
        sx={{
          borderBottom: "1px solid #E5E5E8",
          paddingLeft: "20px",
          paddingRight: "20px",
          paddingTop: "20px",
          paddingBottom: "15px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" component="h5">
          Edit Options for {serviceData?.service_name || "Service"}
        </Typography>
        <Box sx={{ cursor: "pointer" }} onClick={onClose}>
          <img src={close} alt="close" />
        </Box>
      </Box>

      <DialogContent sx={{ paddingTop: "20px" }}>
        <Stack direction={"row"} sx={{ marginTop: "10px" }}>
          <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
            Select Country
          </Typography>
        </Stack>
        <Stack sx={{ m: 0 }}>
          <Tooltip
            title="Makes this shipping option available to all destinations."
            placement="right-start"
            arrow
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={formik.values.apply_to_all_countries}
                  onChange={(event) => {
                    if (event.target.checked) {
                      formik.setFieldValue("country_targeting_mode", "");
                      formik.setFieldValue("country_list", []);
                    }
                    formik.setFieldValue(
                      "apply_to_all_countries",
                      event.target.checked,
                    );
                  }}
                />
              }
              label="Apply to all countries"
              sx={{
                width: "fit-content",
                ".MuiFormControlLabel-label": {
                  fontSize: "13.5px",
                  fontWeight: 600,
                },
              }}
            />
          </Tooltip>
        </Stack>
        <Stack>
          <FormControl fullWidth size="small">
            <Autocomplete
              multiple
              limitTags={2}
              id="multiple-limit-tags"
              size="small"
              options={Countries}
              getOptionLabel={(option) => option.name || ""}
              value={Countries.filter((c) =>
                formik.values.country_list.includes(c.iso2),
              )}
              disabled={formik.values.apply_to_all_countries}
              onChange={(event, newValue) => {
                formik.setFieldValue(
                  "country_list",
                  newValue.map((v) => v.iso2),
                );
              }}
              isOptionEqualToValue={(option, value) =>
                option.iso2 === value.iso2
              }
              sx={{
                "& .MuiAutocomplete-inputRoot": {
                  borderColor: "#d2d4e4",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(95, 56, 249, 0.65)",
                    boxShadow: "0 0 5px 0px rgba(95, 56, 249, 0.2)",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select Countries"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.name}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          </FormControl>
        </Stack>
        {formik.touched.country_list && formik.errors.country_list && (
          <Stack sx={{ marginLeft: "8px", marginTop: "4px" }}>
            <Typography color="error" variant="caption">
              {formik.errors.country_list}
            </Typography>
          </Stack>
        )}

        <Stack sx={{ marginTop: "10px", marginLeft: "8px" }}>
          <Tooltip
            title="Choose whether this option is shown only to selected countries or hidden from them."
            placement="right-start"
            arrow
          >
            <FormControl sx={{ width: "fit-content" }}>
              <RadioGroup
                row
                name="country_targeting_mode"
                value={formik.values.country_targeting_mode}
                onChange={formik.handleChange}
              >
                <FormControlLabel
                  value="INCLUDE"
                  disabled={formik.values.apply_to_all_countries}
                  control={<Radio size="small" />}
                  label="Include"
                  sx={{
                    ".MuiFormControlLabel-label": {
                      fontSize: "14px",
                      fontWeight: 600,
                    },
                  }}
                />
                <FormControlLabel
                  value="EXCLUDE"
                  disabled={formik.values.apply_to_all_countries}
                  control={<Radio size="small" />}
                  label="Exclude"
                  sx={{
                    ".MuiFormControlLabel-label": {
                      fontSize: "14px",
                      fontWeight: 600,
                    },
                  }}
                />
              </RadioGroup>
            </FormControl>
          </Tooltip>
        </Stack>
        {formik.touched.country_targeting_mode &&
          formik.errors.country_targeting_mode && (
            <Stack
              sx={{
                marginLeft: "8px",
                marginTop: "-5px",
                marginBottom: "5px",
              }}
            >
              <Typography color="error" variant="caption">
                {formik.errors.country_targeting_mode}
              </Typography>
            </Stack>
          )}
      </DialogContent>

      <DialogActions sx={{ padding: "0 20px 20px 0" }}>
        <Button onClick={onClose} sx={{ color: "#959BA1" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={formik.handleSubmit}
          sx={{
            backgroundColor: "#5E17EB",
            color: "white",
            border: "1px solid #E5E5E8",
            boxShadow: "none",
            borderRadius: "4px",
            whiteSpace: "nowrap",
            minWidth: "106px",
            "&:hover": {
              outline: "none",
              color: "white",
              backgroundColor: "#481AA3",
            },
          }}
        >
          Save Details
        </Button>
      </DialogActions>
    </Dialog>
  );
}
